import { scry, scryImageUrl } from "@/lib/scryClient";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  where,
  serverTimestamp,
  addDoc,
  updateDoc,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { Button } from "@/components/ui/button";
import Share from "@/assets/icons/atlas/share.svg?react";
import CardDetailsModal from "@/components/CardDetailsModal";
import OfferModal from "@/components/OfferModal";
import { ensureDmThreadFor } from "@/lib/dm";
import { sendOfferV2, ensureOfferCardSnapshot, type SendOfferInput } from "@/data/offersV2";
import { LayoutGrid, Square } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { openOfferComposer } from "@/components/offers/OfferComposerModal";
import FollowButtonV3 from "@/components/follow/FollowButtonV3"; // NEW
import EventCard, { type EventDoc } from "@/events/EventCard"; // NEW
import { createPortal } from "react-dom"; // NEW
import AddToCollectionModal from "@/components/AddToCollectionModal"; // NEW (reuse existing collection add flow)
import { PriceOverlay } from "@/components/PriceOverlay"; // ADD

/* ---------------- Types ----------------- */
type MiniProfile = {
  uid: string;
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  rank_title?: string | null;
  preferences?: {
    available_for_trading?: boolean;
    available_for_play?: boolean;
    wishlist_is_public?: boolean;
    collection_browsing?: boolean;
  };
};

export type MiniCard = {
  id: string;
  name: string;
  image?: string | null;
  set?: string | null;
  number?: string | null;
  foil?: boolean;
  status?: "sale" | "trade" | "keep";
  price?: number | null;
  scryfall_id?: string | null;
  oracle_id?: string | null;
  createdAt?: any;
};

type ViewTab = "profile" | "collection" | "wishlist" | "events"; // EXTENDED
type CollectionViewMode = "single" | "grid";

export type NewMiniProfileCardProps = {
  profile: MiniProfile;
  distanceMiles?: number | null;
  highlightCardId?: string | null;
  className?: string;
  mutedForTrade?: boolean;
  mutedForPlay?: boolean;
  isPublic?: boolean;
  onOpenOfferChain?: (item: MiniCard, owner: MiniProfile) => void;
  onOpenDeckFromGrid?: (ownerUid: string) => void;
  onInteractionChange?: (active: boolean) => void;
  searchHighlightCard?: MiniCard | null;
  gridMode?: boolean;
  isLgs?: boolean;
  compactGrid?: boolean;
  disableFeedTrim?: boolean;
  cardPreviewUrl?: string | null;
  allowCollectionViewToggle?: boolean;
  initialCollectionView?: CollectionViewMode;
  fitViewportOnZoom?: boolean;
  focusMode?: boolean; // NEW: render in focused modal (prevents recursive open)
};

const PAGE = 20;
/* --------------- Helpers ---------------- */
function publicProfilePath(p: MiniProfile) {
  const uname = p.username || p.display_name || p.uid.slice(0, 6);
  return `/${encodeURIComponent(String(uname))}`;
}
function cx(...s: Array<string | false | null | undefined>) {
  return s.filter(Boolean).join(" ");
}

/* --------------- Component --------------- */
export default function NewMiniProfileCard(props: NewMiniProfileCardProps) {
  const {
    profile,
    distanceMiles,
    highlightCardId,
    className,
    mutedForTrade,
    mutedForPlay,
    searchHighlightCard,
    gridMode,
    onOpenOfferChain,
    onOpenDeckFromGrid,
    onInteractionChange = () => {},
    isLgs: isLgsProp,
    isPublic = false,
    compactGrid = false,
    disableFeedTrim = false,
    cardPreviewUrl,
    initialCollectionView = "single",
    allowCollectionViewToggle = false,
    fitViewportOnZoom = false,
    focusMode = false,
  } = props;

  if (!profile) {
    console.error("NewMiniProfileCard: 'profile' prop missing", props);
    return null;
  }
  const p = profile;

  /* ---- Privacy / preference helpers (stub logic â€“ replace as needed) ---- */
  function canShow(_field: string) {
    // TODO: integrate real privacy model
    return true;
  }

  /* ---- LGS / status heuristics ---- */
  const isLgs =
    typeof isLgsProp === "boolean"
      ? isLgsProp
      : !!(
          (p.rank_title || "").toLowerCase().includes("lgs") ||
          (p as any).account_type === "lgs" ||
          (p as any).is_lgs
        );

  /* ---- Collection view (single | grid) ---- */
  const [collectionView, setCollectionView] = useState<CollectionViewMode>(initialCollectionView);
  // binderGrid = user wants 3x3 card grid inside the card
  const binderGrid = collectionView === "grid";
  // gridStylingActive = apply compact styling if parent feed gave gridMode OR user chose binderGrid
  const gridStylingActive = gridMode || binderGrid;

  /* ---- Card zoom / modals ---- */
  const [zoomCard, setZoomCard] = useState<MiniCard | null>(null);
  useEffect(() => {
    if (!zoomCard) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomCard(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomCard]);

  const CARD_HEIGHT_FULL = "clamp(960px,120vh,1200px)";
  const CARD_HEIGHT_COMPACT = "340px"; // ~1/3 of full visual height
  const CARD_HEIGHT = compactGrid ? CARD_HEIGHT_COMPACT : CARD_HEIGHT_FULL;

  /* ---- Extended profile fetch ---- */
  const [profileExtra, setProfileExtra] = useState<any>(
    (p as any)?.extra ||
      (p as any)?.profileExtra ||
      (p as any)?.details || {}
  );
  const [profileExtraLoading, setProfileExtraLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setProfileExtraLoading(true);
        const refs = [doc(db, "profiles", p.uid), doc(db, "users", p.uid)];
        for (const r of refs) {
          const snap = await getDoc(r).catch(() => null);
            if (snap?.exists()) {
              if (!cancelled)
                setProfileExtra((prev: any) => ({ ...prev, ...snap.data() }));
              break;
            }
        }
      } finally {
        if (!cancelled) setProfileExtraLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [p.uid]);

  /* ---- Derived / display fields ---- */
  const bio = profileExtra?.bio || profileExtra?.store_description || "";
  const playFormats: string[] = Array.isArray(profileExtra?.play_formats)
    ? profileExtra.play_formats
    : [];
  const favoriteFormats: string[] = Array.isArray(profileExtra?.favorite_formats)
    ? profileExtra.favorite_formats
    : [];
  const competitivePrefs: string[] = Array.isArray(
    profileExtra?.competitive_preferences
  )
    ? profileExtra.competitive_preferences
    : [];
  const meetPrefs: string[] = Array.isArray(profileExtra?.meetup_preferences)
    ? profileExtra.meetup_preferences
    : [];
  const commanderFocus: string =
    profileExtra?.commander_focus ||
    profileExtra?.edh_focus ||
    profileExtra?.edh_profile ||
    "";
  const preferredLgsName =
    profileExtra?.preferred_lgs ||
    profileExtra?.preferred_store ||
    profileExtra?.home_store ||
    "";
  const deckThemes: string[] = Array.isArray(profileExtra?.deck_themes)
    ? profileExtra.deck_themes
    : [];
  const collectionTags: string[] = Array.isArray(profileExtra?.collection_tags)
    ? profileExtra.collection_tags
    : [];
  const shippingRegions: string[] = Array.isArray(
    profileExtra?.shipping_regions
  )
    ? profileExtra.shipping_regions
    : [];
  const tradingIntent: string =
    profileExtra?.trading_intent ||
    profileExtra?.trade_intent ||
    profileExtra?.trade_policy ||
    "";
  const availabilityPlay: string[] = Array.isArray(
    profileExtra?.availability_play_days
  )
    ? profileExtra.availability_play_days
    : [];
  const availabilityTrade: string[] = Array.isArray(
    profileExtra?.availability_trade_days
  )
    ? profileExtra.availability_trade_days
    : [];
  const timezone =
    profileExtra?.timezone ||
    profileExtra?.time_zone ||
    profileExtra?.tz ||
    "";
  const viewerPlayFormatsSet = new Set<string>(); // TODO integrate viewer formats

  // LGS specific fields
  const lgsFormats: string[] = Array.isArray(profileExtra?.lgs_formats)
    ? profileExtra.lgs_formats
    : Array.isArray(profileExtra?.supported_formats)
    ? profileExtra.supported_formats
    : [];
  const lgsServices: string[] = Array.isArray(profileExtra?.lgs_services)
    ? profileExtra.lgs_services
    : Array.isArray(profileExtra?.services)
    ? profileExtra.services
    : [];
  const lgsAmenities: string[] = Array.isArray(profileExtra?.amenities)
    ? profileExtra.amenities
    : Array.isArray(profileExtra?.store_amenities)
    ? profileExtra.store_amenities
    : [];
  const weeklyEvents: any[] = Array.isArray(profileExtra?.weekly_events)
    ? profileExtra.weekly_events
    : Array.isArray(profileExtra?.events_schedule)
    ? profileExtra.events_schedule
    : [];
  const storeHoursObj = profileExtra?.store_hours || profileExtra?.hours || {};
  const website =
    profileExtra?.website ||
    profileExtra?.website_url ||
    profileExtra?.site ||
    "";
  const contactEmail =
    profileExtra?.contact_email ||
    profileExtra?.email ||
    profileExtra?.store_email ||
    "";
  const contactPhone =
    profileExtra?.contact_phone ||
    profileExtra?.phone ||
    profileExtra?.store_phone ||
    "";
  const social = {
    facebook:
      profileExtra?.facebook ||
      profileExtra?.facebook_url ||
      profileExtra?.social_facebook,
    instagram:
      profileExtra?.instagram ||
      profileExtra?.instagram_url ||
      profileExtra?.social_instagram,
    twitter:
      profileExtra?.twitter ||
      profileExtra?.twitter_url ||
      profileExtra?.x ||
      profileExtra?.social_twitter,
    discord:
      profileExtra?.discord ||
      profileExtra?.discord_url ||
      profileExtra?.discord_invite,
    youtube:
      profileExtra?.youtube ||
      profileExtra?.youtube_url ||
      profileExtra?.social_youtube,
  };
  const addrLine1 =
    profileExtra?.address_line1 || profileExtra?.addr1 || profileExtra?.address;
  const addrLine2 =
    profileExtra?.address_line2 || profileExtra?.addr2 || profileExtra?.suite;
  const city =
    profileExtra?.city ||
    profileExtra?.locality ||
    profileExtra?.town ||
    profileExtra?.municipality;
  const region =
    profileExtra?.state ||
    profileExtra?.region ||
    profileExtra?.province ||
    profileExtra?.state_code;
  const postal =
    profileExtra?.postal_code ||
    profileExtra?.zip ||
    profileExtra?.zip_code ||
    profileExtra?.postal;
  const country =
    profileExtra?.country ||
    profileExtra?.country_code ||
    profileExtra?.nation;

  const showBio = !!bio;

  const nav = useNavigate();
  const me = auth.currentUser?.uid || null;
  // Ensure/open DM thread then navigate inbox with explicit state so Inbox opens it
  async function openInboxThreadFor(them: string) {
    const meUid = auth.currentUser?.uid;
    if (!meUid || !them || meUid === them) return;
    try {
      const maybeThread = await ensureDmThreadFor(meUid, them);
      if (maybeThread && typeof maybeThread === "string") {
        nav(`/inbox?thread=${encodeURIComponent(maybeThread)}`, {
          state: { openThread: true, threadId: maybeThread, fromProfileUid: them },
        });
        return;
      }
    } catch (e) {
      // ignore and try to look up existing thread
      console.warn("ensureDmThreadFor failed, trying thread lookup", e);
    }
    try {
      const q = query(collection(db, "threads_v2"), where("participants", "array-contains", meUid));
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        const v: any = d.data();
        const ps: string[] = Array.isArray(v.participants) ? v.participants : [];
        if (ps.includes(them)) {
          nav(`/inbox?thread=${encodeURIComponent(d.id)}`, {
            state: { openThread: true, threadId: d.id, fromProfileUid: them },
          });
          return;
        }
      }
    } catch (e) {
      console.warn("thread lookup failed", e);
    }
    // fallback: open inbox and instruct to open composer / toUid
    nav(`/inbox`, { state: { toUid: them, openComposer: true, fromProfileUid: them } });
  }

  // follow state (was missing and caused runtime ReferenceError)
  /* ---- Tabs ---- */
  const [tab, setTab] = useState<ViewTab>(isLgs ? "events" : "collection");

  /* ---- Events state (for new Events tab) ---- */
  const [eventsList, setEventsList] = useState<EventDoc[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventIdx, setEventIdx] = useState(0);
  
  // Load organizer events (public for others, all for owner). Non-breaking: only runs when Events tab first opened.
  const loadEvents = useCallback(async () => {
    if (loadingEvents) return;
    setLoadingEvents(true);
    try {
      const isOwner = auth.currentUser?.uid === p.uid;
      const col = collection(db, "events");
      // Simple equality queries (no composite index needed); we merge results client-side.
      const q1 = query(col, where("organizerUid", "==", p.uid));
      const q2 = query(col, where("ownerUid", "==", p.uid));
      const q3 = query(col, where("event_host_uid", "==", p.uid));
      const settled = await Promise.allSettled([getDocs(q1), getDocs(q2), getDocs(q3)]);
      const map = new Map<string, any>();
      for (const s of settled) {
        if (s.status !== "fulfilled") continue;
        s.value.docs.forEach(d => {
          if (map.has(d.id)) return;
          const v: any = d.data();
          // Filter visibility for non-owner
          if (!isOwner && v.visibility === "private") return;
          map.set(d.id, { id: d.id, ...v });
        });
      }
      const toMillis = (v: any) => {
        if (!v) return 0;
        if (v?.toDate) {
          try { return v.toDate().getTime(); } catch {}
        }
        if (v instanceof Date) return v.getTime();
        if (typeof v === "number") return v;
        if (typeof v === "string") {
          const t = Date.parse(v);
          return Number.isFinite(t) ? t : 0;
        }
        return 0;
      };
      const list: EventDoc[] = Array.from(map.values()).map(raw => ({
        id: String(raw.id),
        title: raw.title || raw.name || "Event",
        details: raw.details || raw.description || "",
        formatTypes: Array.isArray(raw.formatTypes) ? raw.formatTypes : [],
        visibility: raw.visibility === "private" ? "private" : "public",
        startTime: raw.startTime ?? raw.start ?? raw.start_at ?? null,
        endTime: raw.endTime ?? raw.endAt ?? null,
        organizerUid: raw.organizerUid || raw.ownerUid || raw.event_host_uid || "",
        organizerUsername: raw.organizerUsername || raw.organizer?.username || "",
        organizerDisplayName: raw.organizerDisplayName || raw.organizer?.displayName || raw.organizerUsername || null,
        organizerAvatarUrl: raw.organizerAvatarUrl || raw.organizer?.avatar_url || null,
        organizerIsLGS: !!(raw.organizerIsLGS || raw.locationType === "LGS"),
        images: Array.isArray(raw.images) ? raw.images : [],
        distanceMiles: typeof raw.distanceMiles === "number" ? raw.distanceMiles : null,
        favoritesCount: typeof raw.favoritesCount === "number" ? raw.favoritesCount : 0,
        eventType: raw.eventType || null,
        address: raw.address || raw.venue?.address || null,
        locationType: raw.locationType || null,
        location: raw.location || (raw.venue && raw.venue.lat && raw.venue.lng
          ? { lat: Number(raw.venue.lat), lng: Number(raw.venue.lng), name: raw.venue.name ?? null }
          : null),
      }));
      list.sort((a, b) => toMillis(a.startTime) - toMillis(b.startTime));
      setEventsList(list);
      setEventIdx(0);
    } catch (e) {
      console.warn("[MiniProfileCard] loadEvents failed", e);
    } finally {
      setLoadingEvents(false);
    }
  }, [p.uid, loadingEvents]);

  // Lazy load when switching to events tab
  useEffect(() => {
    if (tab === "events" && eventsList.length === 0) {
      void loadEvents();
    }
  }, [tab, eventsList.length, loadEvents]);

  /* ---- Collection / Wishlist State ---- */
  const [cards, setCards] = useState<MiniCard[]>([]);
  const [hasMoreCards, setHasMoreCards] = useState(false);
  const lastCardRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [loadingCards, setLoadingCards] = useState(false);

  const [wishes, setWishes] = useState<MiniCard[]>([]);
  const [hasMoreWishes, setHasMoreWishes] = useState(false);
  const lastWishRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [loadingWishes, setLoadingWishes] = useState(false);

  /* ---- Reset lists when profile changes ---- */
  useEffect(() => {
    setCards([]);
    setWishes([]);
    setHasMoreCards(false);
    setHasMoreWishes(false);
    lastCardRef.current = null;
    lastWishRef.current = null;
    setTab("collection");
  }, [p.uid]);

  /* ---- Swipe / carousel (single-card mode) ---- */
  const [idx, setIdx] = useState(0);
  const touchRef = useRef<{ x: number; y: number } | null>(null);
  const flickRef = useRef<{ lastX: number; lastT: number; vx: number } | null>(
    null
  );

  const [prevIdx, setPrevIdx] = useState<number | null>(null);
  const [animPrevIdx, setAnimPrevIdx] = useState<number | null>(null);
  // quick message modal state (local mini-modal like ProfilePublic)
  const [msgOpenModal, setMsgOpenModal] = useState(false);
  const [msgBody, setMsgBody] = useState("");
  const [msgFile, setMsgFile] = useState<File | null>(null);
  const [msgSending, setMsgSending] = useState(false);
  const [fullCardOpen, setFullCardOpen] = useState(false); // uses nested self-render in portal when true (not in focusMode)

  // Image lightbox (collection / wishlist)
  const [imageLightboxOpen, setImageLightboxOpen] = useState(false);
  const [lightboxCard, setLightboxCard] = useState<MiniCard | null>(null);
  const [addingToCollection, setAddingToCollection] = useState(false);
  const lightboxTriggerRef = useRef<HTMLButtonElement | null>(null);

  // FIX: missing ref caused ReferenceError
  const articleRef = useRef<HTMLElement | null>(null);

  // Centralized stopper to block legacy/global grid modal from also opening
  const stopEventInGrid = useCallback((e: React.SyntheticEvent) => {
    if (gridMode && !focusMode) {
      e.stopPropagation();
    }
  }, [gridMode, focusMode]);

  function handleCardClick(e: React.MouseEvent) {
    // ALWAYS stop bubbling in grid mode so OLD legacy modal (parent listener) never fires
    if (gridMode && !focusMode) e.stopPropagation();

    if ((e.target as Element).closest('[data-no-modal]')) return;
    if ((e.target as Element).closest('[data-allow-modal]')) {
      if (!focusMode) {
        setFullCardOpen(true);
      }
      return;
    }
    if ((e.target as Element).closest('a, button, input, textarea, select, label')) return;
    if (!focusMode) {
      setFullCardOpen(true);
    }
  }

  async function sendQuickMessage() {
    if (!auth.currentUser) return;
    const meUid = auth.currentUser.uid;
    const them = p.uid;
    if (meUid === them) return;
    const text = (msgBody || "").trim();
    if (!text && !msgFile) {
      toast({ description: "Type a message or attach a file." });
      setTimeout(() => toast({ description: null }), 1500);
      return;
    }
    setMsgSending(true);
    try {
      let threadId: string | null = null;
      try {
        const got = await ensureDmThreadFor(meUid, them);
        if (typeof got === "string" && got) threadId = got;
      } catch (e) {
        console.warn("ensureDmThreadFor failed, falling back to local resolver", e);
      }
      if (!threadId) {
        const dm2 = `dm2:${[meUid, them].sort().join("_")}`;
        try {
          await setDoc(
            doc(db, "threads_v2", dm2),
            {
              participants: [meUid, them].sort(),
              visibleFor: [meUid, them].sort(),
              lastMessageAt: serverTimestamp(),
              messageCount: 0,
              unreadCounts: { [meUid]: 0, [them]: 0 },
              tab: "inbox",
            },
            { merge: true }
          );
          threadId = dm2;
        } catch (e) {
          const threadDoc = await addDoc(collection(db, "threads_v2"), {
            participants: [meUid, them],
            visibleFor: [meUid, them],
            updatedAt: serverTimestamp(),
            lastMessageAt: serverTimestamp(),
            lastMessage: null,
            lastSenderId: null,
            messageCount: 0,
          });
          threadId = threadDoc.id;
        }
      }
      let attachmentUrl: string | null = null;
      if (msgFile) {
        try {
          const { getStorage, ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
          const storage = getStorage();
          const key = `messages/${meUid}/${Date.now()}-${msgFile.name}`;
          const fileRef = ref(storage, key);
          await uploadBytes(fileRef, msgFile);
          attachmentUrl = await getDownloadURL(fileRef);
        } catch (e) {
          console.warn("Attachment upload failed, sending without attachment.", e);
        }
      }
      const payload: any = {
        threadId,
        senderId: meUid,
        recipientId: them,
        participants: [meUid, them],
        body: text || (attachmentUrl ? "(attachment)" : ""),
        createdAt: serverTimestamp(),
        isRead: false,
      };
      if (attachmentUrl) payload.attachmentUrls = [attachmentUrl];
      await addDoc(collection(db, "messages_v2"), payload);
      try {
        await updateDoc(doc(db, "threads_v2", threadId), {
          updatedAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessage: (text || "ðŸ“Ž Attachment").slice(0, 240),
          lastSenderId: meUid,
        });
      } catch (e) {
        console.warn("Thread metadata update failed (non-fatal):", e);
      }
      try {
        await addDoc(collection(db, "users", them, "notifications"), {
          toUid: them,
          fromUid: meUid,
          type: "message",
          text: text || "Attachment",
          conversationId: threadId,
          createdAt: serverTimestamp(),
          read: false,
          isRead: false,
        });
      } catch (e) {
        console.warn("Notification create failed (non-fatal):", e);
      }
      setMsgOpenModal(false);
      setMsgBody("");
      setMsgFile(null);
      toast({ description: `Message sent to @${p.username || p.display_name || p.uid.slice(0,6)}` });
      setTimeout(()=>toast({description:null}),1600);
    } catch (e) {
      console.error(e);
      toast({ description: "Could not send message. Try again." });
      setTimeout(()=>toast({description:null}),1600);
    } finally {
      setMsgSending(false);
    }
  }

  /* ---- Loaders ---- */
  const loadCards = useCallback(
    async (initial = false) => {
      if (loadingCards) return;
      setLoadingCards(true);
      try {
        if (initial) lastCardRef.current = null;
        const root = collection(db, "users", p.uid, "cards");
        const qy = lastCardRef.current
          ? query(
              root,
              where("status", "in", ["sale", "trade"]),
              orderBy("createdAt", "desc"),
              startAfter(lastCardRef.current),
              limit(PAGE)
            )
          : query(
              root,
              where("status", "in", ["sale", "trade"]),
              orderBy("createdAt", "desc"),
              limit(PAGE)
            );
        const snap = await getDocs(qy);
        if (snap.docs.length) lastCardRef.current = snap.docs.at(-1)!;
        const page: MiniCard[] = snap.docs.map((d) => {
  const v: any = d.data();
  return {
    id: d.id,
    name: v.name ?? "Unknown",
    // KEEP image null-ish; we'll build Scryfall URLs at render-time
    image: null,
    set: v.set ?? v.set_code ?? null,
    number: v.number ?? v.collector_number ?? null,
    foil: !!(v.foil || v.isFoil),
    status: v.status,
    price: typeof v.price === "number" ? v.price : null,
    // ✅ only accept a real scryfall_id (or from embedded scry JSON)
    scryfall_id: v.scryfall_id ?? v.scryfall_full?.id ?? null,
    oracle_id: v.oracle_id ?? v.scryfall_full?.oracle_id ?? null,
    createdAt: v.createdAt ?? null,
  };
});
        setCards((prev) => (initial ? page : prev.concat(page)));
        setHasMoreCards(snap.docs.length === PAGE);

        if (initial) {
          if (highlightCardId) {
            const i = page.findIndex((c) => c.id === highlightCardId);
            setIdx(i >= 0 ? i : 0);
          } else {
            setIdx(0);
          }
        }
      } finally {
        setLoadingCards(false);
      }
    },
    [p.uid, loadingCards, highlightCardId]
  );

  const loadWishes = useCallback(
    async (initial = false) => {
      if (loadingWishes) return;
      setLoadingWishes(true);
      try {
        if (initial) lastWishRef.current = null;
        const root = collection(db, "users", p.uid, "wishlist");
        const qy = lastWishRef.current
          ? query(
              root,
              orderBy("createdAt", "desc"),
              startAfter(lastWishRef.current),
              limit(PAGE)
            )
          : query(root, orderBy("createdAt", "desc"), limit(PAGE));
        const snap = await getDocs(qy);
        if (snap.docs.length) lastWishRef.current = snap.docs.at(-1)!;
        // loadWishes mapper
const page: MiniCard[] = snap.docs.map((d) => {
  const v: any = d.data();
  return {
    id: d.id,
    name: v.name ?? "Unknown",
    image: null,
    set: v.set ?? v.set_code ?? null,
    number: v.number ?? v.collector_number ?? null,
    foil: !!(v.foil || v.isFoil),
    status: "keep",
    price: null,
    // ✅ same rule here
    scryfall_id: v.scryfall_id ?? v.scryfall_full?.id ?? null,
    oracle_id: v.oracle_id ?? v.scryfall_full?.oracle_id ?? null,
    createdAt: v.createdAt ?? null,
  };
});

        setWishes((prev) => (initial ? page : prev.concat(page)));
        setHasMoreWishes(snap.docs.length === PAGE);
      } finally {
        setLoadingWishes(false);
      }
    },
    [p.uid, loadingWishes]
  );

  useEffect(() => {
    if (tab === "collection" && cards.length === 0) loadCards(true);
    if (tab === "wishlist" && wishes.length === 0) loadWishes(true);
  }, [tab, cards.length, wishes.length, loadCards, loadWishes]);

  /* ---- Active list & highlight reordering ---- */
  const activeList =
    tab === "collection"
      ? cards
      : tab === "wishlist"
      ? wishes
      : []; // events handled separately (do not mix with card logic)
  const showingCollection = tab === "collection";
  const highlightCard =
    showingCollection && searchHighlightCard ? searchHighlightCard : null;

  const displayList = useMemo(() => {
    if (!highlightCard) return activeList;
    const matchIdx = activeList.findIndex(
      (c) =>
        c.id === highlightCard.id ||
        (highlightCard.scryfall_id &&
          c.scryfall_id === highlightCard.scryfall_id) ||
        (highlightCard.oracle_id &&
          c.oracle_id &&
          c.oracle_id === highlightCard.oracle_id)
    );
    if (matchIdx === 0) return activeList;
    if (matchIdx > 0) {
      return [
        activeList[matchIdx],
        ...activeList.slice(0, matchIdx),
        ...activeList.slice(matchIdx + 1),
      ];
    }
    return [highlightCard, ...activeList];
  }, [activeList, highlightCard]);

  useEffect(() => {
    if (highlightCard) setIdx(0);
  }, [highlightCard]);

  useEffect(() => {
    if (idx >= displayList.length) setIdx(0);
  }, [displayList.length, idx]);

  const current = displayList[idx] || null;

  /* ---- Binder paging for gridActive ---- */
  const totalGridPages = Math.max(1, Math.ceil(displayList.length / 9));
  const [gridPage, setGridPage] = useState(0);

  useEffect(() => {
    if (!highlightCard) return;
    const i = displayList.findIndex(
      (c) =>
        c.id === highlightCard.id ||
        (highlightCard.scryfall_id &&
          c.scryfall_id === highlightCard.scryfall_id) ||
        (highlightCard.oracle_id &&
          c.oracle_id &&
          c.oracle_id === highlightCard.oracle_id)
    );
    if (i >= 0) setGridPage(Math.floor(i / 9));
  }, [highlightCard, displayList]);

  useEffect(() => {
    if (gridPage > totalGridPages - 1)
      setGridPage(Math.max(0, totalGridPages - 1));
  }, [gridPage, totalGridPages]);

  const gridSlice = useMemo(() => {
    const start = gridPage * 9;
    return displayList.slice(start, start + 9);
  }, [displayList, gridPage]);

  // Ensure gridCards and image helper exist (restored after duplicate-block removal)
  const gridCards = gridSlice;

// helper near getCardImage()
function scryImgFromId(id?: string | null, version: "small" | "normal" = "normal") {
  return id
    ? `https://api.scryfall.com/cards/${encodeURIComponent(String(id))}?format=image&version=${version}`
    : null;
}

  function getCardImage(c: MiniCard): string {
  // grid uses a smaller version for speed
  return (
    scryImgFromId(c.scryfall_id, "small") ||
    // fallback to embedded scry JSON if present
    (c as any)?.scryfall_full?.image_uris?.small ||
    (c as any)?.scryfall_full?.image_uris?.normal ||
    "/images/sg-card-back.png"
  );
}


  /* ---- Swipe / inertial navigation ---- */
  function advanceBy(dir: 1 | -1, steps = 1) {
    // If on events tab, advance events independently (no steps >1 needed)
    if (tab === "events") {
      setEventIdx(i =>
        eventsList.length ? (i + dir + eventsList.length) % eventsList.length : 0
      );
      return;
    }
    for (let s = 0; s < steps; s++) {
      setIdx((i) =>
        displayList.length ? (i + dir + displayList.length) % displayList.length : 0
      );
    }
  }

  useEffect(() => {
    setAnimPrevIdx(prevIdx === null || prevIdx === idx ? null : prevIdx);
    setPrevIdx(idx);
    if (prevIdx !== null && prevIdx !== idx) {
      const t = setTimeout(() => setAnimPrevIdx(null), 420);
      return () => clearTimeout(t);
    }
  }, [idx, prevIdx]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
    flickRef.current = { lastX: t.clientX, lastT: performance.now(), vx: 0 };
    onInteractionChange(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchRef.current || !flickRef.current) return;
    const t = e.touches[0];
    const now = performance.now();
    const dt = (now - flickRef.current.lastT) / 1000;
    if (dt > 0) {
      const dx = t.clientX - flickRef.current.lastX;
      flickRef.current.vx = dx / dt;
      flickRef.current.lastX = t.clientX;
      flickRef.current.lastT = now;
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchRef.current;
    const flick = flickRef.current;
    touchRef.current = null;
    flickRef.current = null;
    onInteractionChange(false);
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const absDx = Math.abs(dx);
    const velocity = flick ? flick.vx : 0;
    const absV = Math.abs(velocity);
    const DIST_THRESHOLD = 55;
    const VEL_THRESHOLD = 900;
    const VEL_DOUBLE = 2000;
    const VEL_TRIPLE = 3000;
    if (absDx < DIST_THRESHOLD && absV < VEL_THRESHOLD) return;
    const dir: 1 | -1 = dx < 0 ? 1 : -1;
    if (tab === "events") {
      // Simple single-step navigation for events
      setEventIdx(i =>
        eventsList.length ? (i + dir + eventsList.length) % eventsList.length : 0
      );
      try { (navigator as any).vibrate?.(8); } catch {}
      return;
    }
    if (binderGrid) {
      setGridPage(
        (pg) => (pg + (dir === 1 ? 1 : -1) + totalGridPages) % totalGridPages
      );
    } else {
      let steps = 1;
      if (absV >= VEL_TRIPLE) steps = 3;
      else if (absV >= VEL_DOUBLE) steps = 2;
      steps = Math.min(steps, Math.max(1, displayList.length - 1));
      if (steps === 1) {
        advanceBy(dir, 1);
      } else {
        let done = 0;
        const interval = 120;
        const hop = () => {
          advanceBy(dir, 1);
          done++;
          if (done < steps) {
            requestAnimationFrame(() => {
              setTimeout(hop, interval);
            });
          }
        };
        hop();
      }
    }
    try {
      (navigator as any).vibrate?.(8);
    } catch {}
  };

  /* ---- Offer flow ---- */
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [offerOpen2, setOfferOpen2] = useState(false);
  const [offerCtx, setOfferCtx] = useState<{
    cardId?: string;
    name?: string;
    scryfall_id?: string;
  } | null>(null);

  const makeOffer = useCallback(
    async (card: MiniCard) => {
      // Parent override chain still honored
      if (onOpenOfferChain) return onOpenOfferChain(card, p);

      const u = auth.currentUser;
      if (!u || u.uid === p.uid) return;
      if (card.status !== "sale" && card.status !== "trade") return;

      const offerType: SendOfferInput["offerType"] =
        card.status === "sale" ? "purchase" : "trade";

      const cardRefPath = `users/${p.uid}/cards/${card.id}`.replace(/^\/+/, "");

      // Fallback legacy direct-offer sender (kept so we do NOT break existing flow
      // if the shared composer fails to load for any reason)
      const sendDirectOffer = async () => {
        try {
          const offerId = await sendOfferV2({
            toUid: p.uid,
            offerType,
            offeredPrice: offerType === "purchase" ? (card.price ?? null) : null,
            message: null,
            preview: null,
            cardId: card.id,
            cardOwnerUid: p.uid,
            cardRefPath,
            cardName: card.name ?? null,
            cardImage: card.image ?? null,
            cardSet: card.set ?? null,
            cardNumber: card.number ?? null,
            cardFoil: !!card.foil,
            cardCondition: (card as any)?.condition ?? null,
            cardQty: (card as any)?.qty ?? 1,
            cardStatus: card.status ?? null,
            listingPrice:
              card.status === "sale" ? (card.price ?? null) : null,
            currency: "USD",
            scryfall_id: card.scryfall_id ?? null,
            oracle_id: card.oracle_id ?? null,
          });

          await ensureOfferCardSnapshot({ id: offerId, cardRefPath });
          toast({ description: "Offer sent âœ…" });
          setTimeout(() => toast({ description: null }), 1600);
        } catch (e) {
          console.error("[offersV2] direct send failed", e);
          toast({ description: "Could not send offer. Please try again." });
          setTimeout(() => toast({ description: null }), 1600);
        }
      };

      try {
        // OPEN SHARED COMPOSER (unified modal UI)
        openOfferComposer({
          toUid: p.uid,
          toUsername:
            p.username ||
            p.display_name ||
            p.uid.slice(0, 6),
          toAvatar:
            p.avatar_url ||
            (isLgs ? "/images/lgs_default.png" : "/images/avatar-default.png"),
          card: {
            cardId: card.id,
            cardOwnerUid: p.uid,        // NEW explicit owner
            cardRefPath,
            name: card.name,
            cardName: card.name,
            image: card.image || null,
            cardImage: card.image || null,
            set: card.set || null,
            number: card.number ?? null,
            cardNumber: card.number ?? null,
            status: card.status,
            cardStatus: card.status,
            foil: card.foil,
            cardFoil: card.foil,
            condition: (card as any).condition || null,
            cardCondition: (card as any).condition || null,
            price: card.status === "sale" ? card.price ?? null : null,
            listingPrice: card.status === "sale" ? card.price ?? null : null,
            scryfall_id: card.scryfall_id || null,
            oracle_id: card.oracle_id || null,
            cardQty: (card as any).qty ?? 1,
            currency: "USD",
          },
          suggestedMessage:
            card.status === "sale"
              ? `Hi! I'm interested in purchasing ${card.name}.`
              : `Hi! I'm interested in trading for ${card.name}. I can offer...`,
          allowPrice:
            card.status === "sale" &&
            (typeof card.price === "number" || card.price == null),
          basePrice: card.status === "sale" ? card.price ?? null : null,
        });
        // No toast here; composer will handle confirmation UX.
      } catch (err) {
        console.warn(
          "[NewMiniProfileCard] openOfferComposer failed; using direct send fallback.",
          err
        );
        await sendDirectOffer();
      }
    },
    [onOpenOfferChain, p, isLgs]
  );

  // Helper to build the listing item for CardDetailsModal
  const currentForModal = useMemo(() => {
    if (!offerCtx) return null;
    // Try to find the referenced card in active lists for richer data
    const base =
      cards.find((c) => c.id === offerCtx.cardId) ||
      wishes.find((c) => c.id === offerCtx.cardId) ||
      current ||
      null;
    if (!base) return null;
    return {
      name: offerCtx.name || base.name || "",
      image: base.image || null,
      set: base.set || null,
      number: base.number || null,
      foil: !!base.foil,
      status:
        base.status === "sale" || base.status === "trade"
          ? base.status
          : "trade",
      price:
        base.status === "sale" && typeof base.price === "number"
          ? base.price
          : null,
      scryfall_id: offerCtx.scryfall_id || base.scryfall_id || null,
      ownerUid: p.uid,
      username:
        p.username ||
        p.display_name ||
        p.uid.slice(0, 6),
      ownerAvatar: p.avatar_url || null,
      docId: offerCtx.cardId || null,
    };
  }, [offerCtx, cards, wishes, current, p.uid, p.username, p.display_name, p.avatar_url]);

  /* ---- Availability chips ---- */
  const tradeAvail = p.preferences?.available_for_trading !== false;
  const playAvail = p.preferences?.available_for_play !== false;

  /* ---- Styling classes ---- */
  const topBarClasses = isLgs
    ? "from-[#00aaff]/70 via-[#0088ff]/55 to-[#002b66]/40"
    : "from-purple-700/60 via-violet-700/40 to-indigo-900/30";
  const bottomBarClasses = isLgs
    ? "from-[#007fe0]/70 via-[#006ad1]/55 to-[#004b99]/45"
    : "from-indigo-800/70 via-purple-800/50 to-violet-950/40";

  const usernameLabel =
    p.display_name ||
    p.username ||
    (p.rank_title ? `${p.rank_title}` : "") ||
    p.uid.slice(0, 6);

  // REVISED distance logic: accept prop OR fallbacks from profile object; handle numeric strings
  const distanceLabel = useMemo(() => {
    const raw =
      distanceMiles ??
      (p as any)?.distanceMiles ??
      (p as any)?.distance_miles ??
      (p as any)?.distance ??
      null;
    if (raw === null || raw === undefined) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.round(n);
  }, [distanceMiles, p]);

  /* ---- Render ---- */
  return (
    <article
      ref={articleRef}
      onMouseDown={stopEventInGrid}        // NEW: suppress parent handlers early (legacy modal)
      onPointerDown={stopEventInGrid}      // NEW: extra safety for pointer listeners
      onClick={handleCardClick}
      data-binder-grid={binderGrid ? "true" : "false"}
      data-focus-mode={focusMode ? "true" : "false"}
      className={cx(
        "profile-card-root flex flex-col w-full",
        "rounded-xl",
        // Added subtle glow / drop shadow (non-destructive)
        "ring-1 ring-white/10",
        focusMode
          ? "shadow-[0_4px_22px_-2px_rgba(0,0,0,0.65),0_0_20px_5px_rgba(56,189,248,0.25)]"
          : "shadow-[0_4px_18px_-2px_rgba(0,0,0,0.55),0_0_18px_4px_rgba(56,189,248,0.12)] hover:shadow-[0_6px_26px_-2px_rgba(0,0,0,0.6),0_0_28px_6px_rgba(56,189,248,0.18)] transition-shadow",
        (gridStylingActive) && "grid-compact",
        gridMode && !disableFeedTrim ? "grid-flush" : "",
        !disableFeedTrim && !binderGrid && !gridMode && "feed-deck-mode",
        className
      )}
      style={{
        '--card-height': CARD_HEIGHT,
        ...(focusMode
          ? { height: '100%', maxHeight: '100%', minHeight: '100%' }
          : { height: CARD_HEIGHT, maxHeight: CARD_HEIGHT, minHeight: CARD_HEIGHT })
      } as React.CSSProperties}
      onDoubleClick={() => onOpenDeckFromGrid?.(p.uid)}
    >
      {/* Header */}
      <div
        className={cx(
          "px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-3",
          "border-b border-white/10",
          "bg-gradient-to-r",
          topBarClasses,
          "backdrop-blur-md shrink-0",
          "rounded-t-xl"
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={publicProfilePath(p)}
              className="group inline-flex items-center gap-2"
              title="Open public profile"
            >
              <img
                src={
                  p.avatar_url ||
                  (isLgs ? "/images/lgs_default.png" : "/images/avatar-default.png")
                }
                alt={usernameLabel}
                className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20"
              />
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate text-base font-medium text-white group-hover:underline">
                  {usernameLabel}
                </span>
                {distanceLabel !== null && (
                  <span
                    className="shrink-0 inline-flex items-center rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium text-white/75 border border-white/25"
                    title={`${distanceLabel} miles away`}
                    data-no-modal
                  >
                    {distanceLabel} mi
                  </span>
                )}
              </div>
            </Link>
          </div>
        </div>

        {/* SECOND ROW: hide entirely in global grid mode */}
        {!gridMode && (
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {p.rank_title && (
                <span className="rounded-full border border-white/25 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/70">
                  {p.rank_title}
                </span>
              )}
              <span
                className={cx(
                  "rounded-full px-2 py-0.5 font-medium",
                  tradeAvail
                    ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/40"
                    : "bg-white/10 text-white/40 border border-white/15"
                )}
              >
                Trade
              </span>
              <span
                className={cx(
                  "rounded-full px-2 py-0.5 font-medium",
                  playAvail
                    ? "bg-sky-500/25 text-sky-300 border border-sky-500/40"
                    : "bg-white/10 text-white/40 border border-white/15"
                )}
              >
                Play
              </span>
            </div>
            {/* Hide grid toggle button when already in global grid mode */}
            {!gridMode && (
              <button
                type="button"
                aria-label="Toggle grid view"
                onClick={() =>
                  setCollectionView(v => (v === "grid" ? "single" : "grid"))
                }
                className={cx(
                  "rounded-md border px-2 py-1 text-[11px] font-medium transition",
                  binderGrid
                    ? "border-amber-400/60 bg-amber-500/20 text-amber-200"
                    : "border-white/20 bg-black/40 text-white/60 hover:bg-white/10"
                )}
              >
                {binderGrid ? "Cards" : "3x3 Grid"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main viewer */}
      <div
        className={cx(
          "card-stage-wrapper relative flex-1 min-h-0 flex overflow-hidden",
          // remove vertical scroll & grayscale logic stays
          tab === "collection" && mutedForTrade ? "opacity-50 grayscale" : "",
          tab === "wishlist" && mutedForPlay ? "opacity-50 grayscale" : ""
        )}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className={cx(
            "card-stage relative mx-auto flex-1 w-full select-none overflow-hidden",
            binderGrid
              ? "flex-none"                              // NO centering in binder grid
              : "flex items-center justify-center",       // existing behavior for single mode
            "p-0"
          )}
        >
          {/* Tabs */}
          <div className="absolute top-2 right-2 z-20 flex gap-1">
            {(["profile", "collection", "wishlist", "events"] as ViewTab[]).map((k) => (
              <button
                key={k}
                onClick={(e) => {
                  e.stopPropagation();
                  setTab(k);
                }}
                className={cx(
                  "px-2 py-1 rounded text-[10px] border leading-none backdrop-blur-sm",
                  tab === k
                    ? "bg-white/25 border-white/60 text-white shadow"
                    : "bg-black/45 border-white/25 text-white/65 hover:bg-white/15"
                )}
                aria-label={`Show ${k}`}
              >
                {k === "profile" ? "Info" : k[0].toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>

          {/* Profile tab */}
          {tab === "profile" ? (
            <div className="absolute inset-0 overflow-y-auto px-5 py-5 text-white/80 text-sm leading-relaxed space-y-10">
              {isLgs ? (
                <>
                  {bio && canShow("lgs_about") && (
                    <section>
                      <SectionHeading title="About" />
                      <p className="whitespace-pre-wrap">{bio}</p>
                    </section>
                  )}

                                    {Object.keys(storeHoursObj || {}).length > 0 &&
                    canShow("lgs_hours") && (
                      <section>
                        <SectionHeading title="Store Hours" />
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-white/70">
                          {Object.entries(storeHoursObj).map(([d, v]) => (
                            <div key={d} className="flex justify-between">
                              <span className="capitalize">{d}:</span>
                              <span className="ml-2">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                  {weeklyEvents.length > 0 && (
                    <section>
                      <SectionHeading title="Weekly Events" />
                      <ul className="space-y-1">
                        {weeklyEvents.map((ev, i) => (
                          <li
                            key={i}
                            className="rounded-md bg-white/5 border border-white/10 px-3 py-2"
                          >
                            <div className="text-white/90 font-medium">
                              {ev.name || ev.title || "Event"}
                            </div>
                            <div className="text-xs text-white/60">
                              { [
                                ev.day || ev.weekday,
                                ev.time,
                                ev.format,
                              ].filter(Boolean).join(" â€¢ ")}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {lgsFormats.length > 0 && (
                    <section>
                      <SectionHeading title="Supported Formats" />
                      <ChipRow values={lgsFormats} />
                    </section>
                  )}

                  {lgsServices.length > 0 && (
                    <section>
                      <SectionHeading title="Services" />
                      <ChipRow values={lgsServices} tone="emerald" />
                    </section>
                  )}

                  {lgsAmenities.length > 0 && (
                    <section>
                      <SectionHeading title="Amenities" />
                      <ChipRow values={lgsAmenities} tone="indigo" />
                    </section>
                  )}

                  
                  {Object.values(social).some(Boolean) && (
                    <section>
                      <SectionHeading title="Social" />
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(social)
                          .filter(([, v]) => !!v)
                          .map(([k, v]) => (
                            <a
                              key={k}
                              href={
                                String(v).startsWith("http")
                                  ? String(v)
                                  : `https://${v}`
                              }
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs px-2 py-1 rounded bg-white/10 border border-white/15 hover:bg-white/15 transition"
                            >
                              {k[0].toUpperCase() + k.slice(1)}
                            </a>
                          ))}
                      </div>
                    </section>
                  )}

                  {/* Store Info (render only fields present in the doc) */}
                  <section>
                    <SectionHeading title="Store Info" />
                    <div className="space-y-2 text-white/75 text-sm">
                      
                      <div>
                        <strong>Address:</strong>{" "}
                        {profileExtra?.address ||
                          profileExtra?.location?.address ||
                          addrLine1 ||
                          "â€”"}
                      </div>
                      <div>
                        <strong>Phone:</strong>{" "}
                        {profileExtra?.contact?.phone ||
                          profileExtra?.phone ||
                          profileExtra?.lgs?.phone ||
                          contactPhone ||
                          "â€”"}
                      </div>
                      <div>
                        <strong>Email:</strong>{" "}
                        {profileExtra?.contact?.email ||
                          profileExtra?.email ||
                          profileExtra?.lgs?.email ||
                          contactEmail ||
                          "â€”"}
                      </div>
                      <div>
                        <strong>Website:</strong>{" "}
                        {(profileExtra?.links?.website ||
                          profileExtra?.website ||
                          profileExtra?.lgs?.website ||
                          website) ? (
                          <a
                            className="text-amber-300 hover:underline break-all"
                            href={
                              (profileExtra?.links?.website ||
                                profileExtra?.website ||
                                profileExtra?.lgs?.website ||
                                website).startsWith("http")
                                ? (profileExtra?.links?.website ||
                                    profileExtra?.website ||
                                    profileExtra?.lgs?.website ||
                                    website)
                                : `https://${profileExtra?.links?.website ||
                                    profileExtra?.website ||
                                    profileExtra?.lgs?.website ||
                                    website}`
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            {profileExtra?.links?.website ||
                              profileExtra?.website ||
                              profileExtra?.lgs?.website ||
                              website}
                          </a>
                        ) : (
                          "â€”"
                        )}
                      </div>
                      <div>
                        <strong>Location:</strong>{" "}
                        {profileExtra?.location?.lat && profileExtra?.location?.lng
                          ? `${profileExtra.location.lat}, ${profileExtra.location.lng}`
                          : "â€”"}
                      </div>
                      </div>
                  </section>

                  {!bio &&
                    !addrLine1 &&
                    !weeklyEvents.length &&
                    !lgsFormats.length &&
                    !lgsServices.length &&
                    !lgsAmenities.length &&
                    !website && (
                      <div className="text-xs text-white/40">
                        No additional store info provided.
                      </div>
                    )}
                </>
              ) : (
                <>
                  {showBio && canShow("bio") && (
                    <section>
                      <SectionHeading title="Bio" />
                      <p className="whitespace-pre-wrap">{bio}</p>
                    </section>
                  )}

                  {commanderFocus && canShow("commander_focus") && (
                    <section>
                      <SectionHeading title="Commander / EDH" />
                      <div className="rounded bg-white/10 border border-white/15 px-3 py-1 inline-block text-[12px]">
                        {commanderFocus}
                      </div>
                    </section>
                  )}

                  {playFormats.length > 0 && canShow("play_formats") && (
                    <section>
                      <SectionHeading title="Play Formats" />
                      <div className="flex flex-wrap gap-1">
                        {playFormats.map((f) => {
                          const match = viewerPlayFormatsSet.has(
                            f.toLowerCase()
                          );
                          return (
                            <span
                              key={f}
                              className={
                                match
                                  ? "rounded-full px-2 py-0.5 text-[11px] bg-amber-500/25 border border-amber-400/70 text-amber-200 font-semibold animate-goldPulse"
                                  : "rounded-full bg-white/10 border border-white/15 px-2 py-0.5 text-[11px]"
                              }
                              title={
                                match ? "You also play this format" : undefined
                              }
                            >
                              {f}
                            </span>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  {favoriteFormats.length > 0 &&
                    canShow("favorite_formats") && (
                      <section>
                        <SectionHeading title="Favorite Formats" />
                        <ChipRow values={favoriteFormats} tone="purple" />
                      </section>
                    )}

                  {competitivePrefs.length > 0 &&
                    canShow("competitive_preferences") && (
                      <section>
                        <SectionHeading title="Competitive Focus" />
                        <ChipRow values={competitivePrefs} tone="rose" />
                      </section>
                    )}

                  {meetPrefs.length > 0 && canShow("meetup_preferences") && (
                    <section>
                      <SectionHeading title="Meet Up Preferences" />
                      <ChipRow values={meetPrefs} tone="indigo" />
                    </section>
                  )}

                  {deckThemes.length > 0 && canShow("deck_themes") && (
                    <section>
                      <SectionHeading title="Deck Themes" />
                      <ChipRow values={deckThemes} tone="cyan" />
                    </section>
                  )}

                  {collectionTags.length > 0 &&
                    canShow("collection_tags") && (
                      <section>
                        <SectionHeading title="Collection Tags" />
                        <ChipRow values={collectionTags} tone="teal" />
                      </section>
                    )}

                  {shippingRegions.length > 0 &&
                    canShow("shipping_regions") && (
                      <section>
                        <SectionHeading title="Shipping Regions" />
                        <ChipRow values={shippingRegions} tone="amber" />
                      </section>
                    )}

                  {tradingIntent && canShow("trading_intent") && (
                    <section>
                      <SectionHeading title="Trading Intent" />
                      <p className="text-white/75 whitespace-pre-wrap">
                        {tradingIntent}
                      </p>
                    </section>
                  )}

                  {(availabilityPlay.length > 0 ||
                    availabilityTrade.length > 0) &&
                    canShow("availability") && (
                      <section>
                        <SectionHeading title="Availability" />
                        <div className="space-y-2">
                          {availabilityPlay.length > 0 && (
                            <div>
                              <div className="text-xs uppercase tracking-wide text-white/50 mb-1">
                                Play
                              </div>
                              <ChipRow
                                values={availabilityPlay}
                                tone="sky"
                                tight
                              />
                            </div>
                          )}
                          {availabilityTrade.length > 0 && (
                            <div>
                              <div className="text-xs uppercase tracking-wide text-white/50 mb-1">
                                Trade
                              </div>
                              <ChipRow
                                values={availabilityTrade}
                                tone="emerald"
                                tight
                              />
                            </div>
                          )}
                        </div>
                      </section>
                    )}

                  {preferredLgsName && canShow("preferred_lgs") && (
                    <section>
                      <SectionHeading title="Preferred LGS" />
                      <div>{preferredLgsName}</div>
                    </section>
                  )}

                  {timezone && canShow("timezone") && (
                    <section>
                      <SectionHeading title="Timezone" />
                      <div>{timezone}</div>
                    </section>
                  )}

                  {!showBio &&
                    !playFormats.length &&
                    !favoriteFormats.length &&
                    !competitivePrefs.length &&
                    !meetPrefs.length &&
                    !deckThemes.length &&
                    !shippingRegions.length &&
                    !commanderFocus &&
                    !tradingIntent &&
                    !preferredLgsName && (
                      <div className="text-xs text-white/40">
                        No additional profile details shared.
                      </div>
                    )}
                </>
              )}
            </div>
          ) : tab === "events" ? (
            <div className="absolute inset-0 flex items-center justify-center">
              {loadingEvents && eventsList.length === 0 && (
                <div className="text-xs text-white/60">Loading events…</div>
              )}
              {!loadingEvents && eventsList.length === 0 && (
                <div className="text-xs text-white/50 px-4 text-center">
                  No events found.
                </div>
              )}
              {eventsList.length > 0 && (
                <div className="w-full h-full relative flex items-center justify-center">
                  {/* Navigation (desktop) */}
                  {eventsList.length > 1 && (
                    <button
                      type="button"
                      aria-label="Previous event"
                      className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-30 bg-slate-800/70 hover:bg-slate-700 text-white rounded-full w-9 h-9 items-center justify-center shadow"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEventIdx(i =>
                          (i - 1 + eventsList.length) % eventsList.length
                        );
                      }}
                    >
                      ‹
                    </button>
                  )}
                  {eventsList.length > 1 && (
                    <button
                      type="button"
                      aria-label="Next event"
                      className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-30 bg-slate-800/70 hover:bg-slate-700 text-white rounded-full w-9 h-9 items-center justify-center shadow"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEventIdx(i =>
                          (i + 1) % eventsList.length
                        );
                      }}
                    >
                      ›
                    </button>
                  )}
                  <div className="card-image-stage relative w-full h-full bg-black/30 overflow-auto flex items-start justify-center p-0">
                    <div className="w-full max-w-[520px]">
                      <EventCard
                        ev={eventsList[eventIdx]}
                        isFavorite={false}
                      />
                    </div>
                  </div>
                  {eventsList.length > 1 && (
                    <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1">
                      {eventsList.map((_, i) => (
                        <span
                          key={i}
                          className={cx(
                            "h-1.5 w-1.5 rounded-full",
                            i === eventIdx ? "bg-white" : "bg-white/40"
                          )}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : displayList.length === 0 ? (
             <div className="absolute inset-0 flex items-center justify-center">
               <div className="card-image-stage relative aspect-[63/88] w-full max-w-[520px] bg-black/40">
                 <img
                   src={
                     (gridMode && cardPreviewUrl) ? cardPreviewUrl : "/images/sg-card-back.png"
                   }
                   alt="Card preview"
                   className="absolute inset-0 h-full w-full object-cover opacity-70"
                   draggable={false}
                 />
                 {gridMode && cardPreviewUrl && (
                   <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/50 pointer-events-none" />
                 )}
               </div>
             </div>
           ) : binderGrid ? (
             <div
               data-mode="grid"
               className={cx(
                 "mini-grid grid grid-cols-3 grid-rows-3 h-full w-full",
                 gridMode ? "gap-0" : "gap-[2px]",
                 "binder-grid-fill"
               )}
             >
               {gridCards.length === 0 && (
                 <div className="col-span-3 flex items-center justify-center text-[11px] text-white/50 border border-dashed border-white/10 rounded">
                   No cards loaded
                 </div>
               )}
               {gridCards.map((c, i) => {
                 const img = getCardImage(c);
                 return (
                   <div
                     key={c.id || i}
                     className={cx(
                       "relative bg-white/5 w-full h-full",
                       "flex items-center justify-center",            // center the image
                       gridMode ? "rounded-none" : "rounded-sm",
                       gridMode ? "border-none" : "border border-white/10",
                       "p-0"                                           // no internal padding
                     )}
                   >
                     {img ? (
                       <img
                         src={img}
                         alt={c.name || "Card"}
                         draggable={false}
                         className="max-h-full max-w-full h-full w-auto object-contain select-none"
                         loading="lazy"
                       />
                     ) : (
                       <div className="flex h-full w-full items-center justify-center text-[10px] text-white/40">
                         No Image
                       </div>
                     )}
                     {c.foil && (
                       <div className="absolute bottom-0 right-0 m-0.5 rounded bg-black/65 px-1 py-[2px] text-[9px] text-white">
                         Foil
                       </div>
                     )}
                     {c.scryfall_id && (
  <div className="absolute left-0.5 bottom-0.5 z-10 pointer-events-none">
    <PriceOverlay
      scryfallId={c.scryfall_id}
      compact
      textScale={0.5}
      flushBackground
    />
  </div>
)}
                   </div>
                 );
               })}
             </div>
           ) : (
             <>
              {[idx, animPrevIdx]
                .filter((v, i, arr) => v != null && arr.indexOf(v) === i)
                .map((cardIndex) => {
                  const i = cardIndex as number;
                  const card = displayList[i];
                  const active = i === idx;
                  const dir =
                    i === idx && prevIdx != null ? (idx > prevIdx ? 1 : -1) : 1;
                  const entering = active && animPrevIdx !== null;
                  const leaving = !active;
                  const baseKey = card.id || card.scryfall_id || i;
                  let animClass = "";
                  if (entering)
                    animClass =
                      dir === 1
                        ? "card-slide-in-right"
                        : "card-slide-in-left";
                  else if (leaving)
                    animClass =
                      dir === 1
                        ? "card-slide-out-left"
                        : "card-slide-out-right";
                  else animClass = "card-still";
                  const src =
                    card.image ||
                    (card.scryfall_id
                      ? `https://api.scryfall.com/cards/${encodeURIComponent(
                          String(card.scryfall_id)
                        )}?format=image&version=normal`
                      : "/images/sg-card-back.png");
                  return (
                    <div
                      key={baseKey + (active ? "-active" : "-prev")}
                      className={`absolute inset-0 flex items-center justify-center ${animClass}`}
                      aria-hidden={!active}
                    >
                     <div
                       className="card-image-stage relative aspect-[63/88] w-full bg-black/40"
                       style={focusMode ? { maxWidth: "100%" } : { maxWidth: "520px" }}
                     >
                        <button
                          data-allow-modal
                          onClick={(e) => {
                            e.stopPropagation();
                            // In collection/wishlist tabs open image lightbox instead of full profile modal
                            if (tab === "collection" || tab === "wishlist") {
                              setLightboxCard(card);
                              setImageLightboxOpen(true);
                              lightboxTriggerRef.current = e.currentTarget;
                            } else if (!focusMode) {
                              setFullCardOpen(true);
                            }
                          }}
                          className="group absolute inset-0 focus:outline-none"
                        >
                          <img
                            src={src}
                            alt={card.name || "Card"}
                            draggable={false}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                          {/* Add overlay (single view) */}
                          {/* single-card overlay */}
{card.scryfall_id && (
  <div className="absolute left-1 bottom-1 z-10 pointer-events-none">
    <PriceOverlay
      scryfallId={card.scryfall_id}
      compact
      flushBackground
    />
  </div>
)}

                        </button>
                      </div>
                    </div>
                  );
                })}
            </>
          )}

          {(tab === "collection" || tab === "wishlist") &&
             !binderGrid &&
             displayList.length > 1 && (
               <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1">
                 {displayList.map((_, i) => (
                   <span
                     key={i}
                     className={cx(
                       "h-1.5 w-1.5 rounded-full",
                       i === idx ? "bg-white" : "bg-white/40"
                     )}
                   />
                 ))}
               </div>
             )}
        </div>
      </div>

      {/* Footer card meta: HIDE in global grid mode too */}
      {(tab === "collection" || tab === "wishlist") &&
        current &&
        !binderGrid &&
        !gridMode && (
          <div
            className={cx(
              "shrink-0 flex items-center justify-between px-3 py-2 text-white/90 bg-gradient-to-r",
              bottomBarClasses,
              "footer-meta-bar",
              tab === "collection" && mutedForTrade ? "opacity-50 grayscale" : "",
              tab === "wishlist" && mutedForPlay ? "opacity-50 grayscale" : ""
            )}
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">
                {current.name || "Unknown"}
              </div>
              <div className="truncate text-xs text-white/70">
                {(current.set || "").toUpperCase()}{" "}
                {current.number ? `#${current.number}` : ""}{" "}
                {current.foil ? "Foil " : ""}
                {current.status === "sale"
                  ? "For Sale"
                  : current.status === "trade"
                  ? "For Trade"
                  : ""}{" "}
                {typeof current.price === "number"
                  ? `$${current.price.toFixed(2)}`
                  : ""}
              </div>
            </div>
          </div>
        )}
      {/* Action Bar */}
      <div
        className={cx(
          "action-bar sticky bottom-0 z-20 shrink-0 flex flex-wrap items-center gap-3 px-4 pb-4 pt-4 bg-gradient-to-r",
          bottomBarClasses
        )}
      >
        {me !== p.uid && !isPublic && (
          <>
           {/* Move Follow button first */}
           <FollowButtonV3
             targetUid={p.uid}
             targetUsername={p.username || p.display_name || p.uid.slice(0, 6)}
             className="h-10"
           />
            {isLgs ? (
              <button
                onClick={() => {
                  const email =
                   
                    (profileExtra && profileExtra.contact && profileExtra.contact.email) ||
                    profileExtra?.contact_email ||
                    profileExtra?.email ||
                    profileExtra?.lgs?.email ||
                    profileExtra?.store_email ||
                    (p as any)?.email ||
                                       contactEmail ||
                    "";
                  if (email) {
                   
                    window.location.href = `mailto:${encodeURIComponent(String(email))}`;
                    return;
                  }
                  nav(`/inbox?to=${p.uid}`);
                }}
                className="follow-img-btn h-10 w-12 flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300/50"
                aria-label="Email Store"
                title="Email store"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-6 w-6 text-amber-300"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8.5v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M21 7l-9 6-9-6" />
                                              </svg>
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();

                  e.preventDefault();
                  if (!auth.currentUser) {
                    toast({ description: "Sign in to send a message." });
                    setTimeout(() => toast({ description: null }), 1500);
                    return;
                  }
                  if (auth.currentUser.uid === p.uid) return;
                  setMsgBody("");
                  setMsgFile(null);
                  setMsgOpenModal(true);
                }}
                className="follow-img-btn h-10 w-12 flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300/50"
                aria-label="Message"
                title="Message"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-8 w-8 text-cyan-200"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 5h16v10H7.5L4 18.5V5z" />
                  <path d="M9 10h6M9 7h8" />
                </svg>
              </button>
            )}
          </>
        )}

        <button
          onClick={async () => {
            const url = window.location.origin + publicProfilePath(p);
            try {
              if ((navigator as any).share) {
                await (navigator as any).share({ title: usernameLabel, url });
              } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(url);
              }
            } catch {}
          }}
          className="follow-img-btn h-10 w-12 flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300/50"
          aria-label="Share Profile"
          title="Share"
        >
          <Share className="h-8 w-8 text-amber-300" />
        </button>

        {(tab === "collection" || tab === "wishlist") &&
          current &&
          (current.status === "sale" || current.status === "trade") &&
          !binderGrid && (
            <Button
              className="flex-1 min-w-[120px] justify-center h-10 text-sm bg-amber-400 text-black hover:bg-amber-300 focus:ring-2 focus:ring-amber-300"
              onClick={() => current && makeOffer(current)}
              disabled={!auth.currentUser || auth.currentUser.uid === p.uid}
              title={
                !auth.currentUser
                  ? "Sign in to make an offer"
                  : auth.currentUser?.uid === p.uid
                  ? "Cannot offer on your own card"
                  : "Make an offer"
              }
            >
              Offer
            </Button>
          )}
      </div>

      {/* Load more controls */}
      {tab === "collection" && hasMoreCards && (
        <div className="shrink-0 px-3 pb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadCards(false)}
            disabled={loadingCards}
          >
            {loadingCards ? "Loadingâ€¦" : "Load more cards"}
          </Button>
        </div>
      )}
      {tab === "wishlist" && hasMoreWishes && (
        <div className="shrink-0 px-3 pb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadWishes(false)}
            disabled={loadingWishes}
          >
            {loadingWishes ? "Loadingâ€¦" : "Load more wishlist"}
          </Button>
        </div>
      )}

      {/* Quick message modal (mini) */}
      {msgOpenModal && (
        <div
          className="fixed inset-0 z-[2147483650] bg-black/70 backdrop-blur-sm grid place-items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-message-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setMsgOpenModal(false);
          }}
        >
          <div
            className="offer-modal-root rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl p-4"
            style={{
              width: "min(92vw,560px)",
              maxHeight: "84vh",
              overflow: "auto",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-full overflow-hidden border border-slate-700 bg-white/5 grid place-items-center">
                  {p.avatar_url ? (
                    <img
                      src={p.avatar_url}
                      alt={p.display_name || p.username || p.uid}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-[10px] text-white/70 font-bold">
                      {(p.username || p.display_name || p.uid).slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 id="quick-message-title" className="text-base font-semibold truncate">
                    Message @{p.username || p.display_name || p.uid.slice(0, 6)}
                  </h3>
                </div>
              </div>
              <button onClick={() => setMsgOpenModal(false)} className="text-slate-300 hover:text-white" aria-label="Close">âœ•</button>
            </div>
            <div className="grid gap-2 mb-3">
              <label className="text-sm">Message</label>
              <textarea
                rows={5}
                className="border rounded px-3 py-2 text-black"
                placeholder="Write a quick messageâ€¦"
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
              />
            </div>
            <div className="grid gap-2 mb-2">
              <label className="text-sm">Attachment (optional)</label>
              <input type="file" accept="image/*,text/plain,application/pdf" onChange={(e) => setMsgFile(e.target.files?.[0] ?? null)} />
              {msgFile && <div className="text-xs text-slate-300 truncate" title={msgFile.name}>ðŸ“Ž {msgFile.name}</div>}
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setMsgOpenModal(false)} disabled={msgSending}>Cancel</Button>
              <Button onClick={() => void sendQuickMessage()} disabled={msgSending}>{msgSending ? "Sendingâ€¦" : "Send"}</Button>
            </div>
          </div>
        </div>
      )}
      {/* end quick message modal */}

      {/* Full-card modal copy (opens when user clicks non-interactive card area) */}
      {fullCardOpen && !focusMode && (
        // If an older global grid modal also opened previously, event stopPropagation above prevents it now.
        createPortal(
          <FocusedProfileModal
            onClose={() => setFullCardOpen(false)}
            ariaLabel={`Profile ${usernameLabel}`}
          >
            <NewMiniProfileCard
              {...props}
              focusMode
              gridMode={false}
              disableFeedTrim
              compactGrid={false}
              fitViewportOnZoom
            />
          </FocusedProfileModal>,
          document.body
        )
      )}

      {/* IMAGE LIGHTBOX */}
      {imageLightboxOpen && lightboxCard &&
        createPortal(
          <ImageLightbox
            card={lightboxCard}
            tab={tab}
            onClose={() => {
              // Close lightbox AND cancel add-to-collection flow if active
              if (addingToCollection) setAddingToCollection(false);
              setImageLightboxOpen(false);
              setLightboxCard(null);
              lightboxTriggerRef.current?.focus?.();
            }}
            onAddToWishlist={async () => {
              if (!auth.currentUser) {
                toast({ description: "Sign in to manage wishlist." });
                return;
              }
              try {
                await addDoc(collection(db, "users", auth.currentUser.uid, "wishlist"), {
                  name: lightboxCard.name,
                  image: lightboxCard.image || null,
                  set: lightboxCard.set || null,
                  number: lightboxCard.number || null,
                  createdAt: serverTimestamp(),
                  scryfall_id: lightboxCard.scryfall_id || null,
                  oracle_id: lightboxCard.oracle_id || null,
                  foil: !!lightboxCard.foil,
                });
                toast({ description: "Added to wishlist ✓" });
                setTimeout(() => toast({ description: null }), 1400);
              } catch (e: any) {
                toast({ description: e?.message || "Could not add." });
              }
            }}
            onAddToCollection={() => {
              if (!auth.currentUser) {
                toast({ description: "Sign in to add to collection." });
                return;
              }
              setAddingToCollection(true);
            }}
          />,
          document.body
        )
      }

      {/* ADD TO COLLECTION (Highest Z) */}
      {addingToCollection && lightboxCard &&
        createPortal(
          <div
            className="fixed inset-0 z-[4000] flex items-center justify-center"
            onMouseDown={(e) => {
              // Click outside modal cancels add flow (and leaves lightbox if still desired)
              if (e.target === e.currentTarget) {
                setAddingToCollection(false);
              }
            }}
          >
            <AddToCollectionModal
  open
  card={{
    id: lightboxCard.scryfall_id || lightboxCard.id, // prefer scryfall_id
    name: lightboxCard.name,
    oracle_id: lightboxCard.oracle_id,
    set: lightboxCard.set || undefined,
    collector_number: lightboxCard.number || undefined,
    // do NOT pass arbitrary CDN urls here unless they are Scryfall image_uris
    image_uris: undefined
  }}
              onClose={() => {
                // Closing this modal cancels the add flow
                setAddingToCollection(false);
              }}
              onAdded={() => {
                setAddingToCollection(false);
                toast({ description: "Added to collection ✓" });
                setTimeout(() => toast({ description: null }), 1400);
              }}
            />
          </div>,
          document.body
        )
      }

      {/* Styles */}
      <style>{`
        /* EXISTING styles ... */

        /* Focus mode card image stage size reduction (25% smaller) */
        .profile-card-root[data-focus-mode="true"] .card-image-stage {
          transition: max-width .25s ease;
        }

        /* Flush layout in focused mode */
        .profile-card-root[data-focus-mode="true"] {
          height:100%;
        }
        .profile-card-root[data-focus-mode="true"] .card-stage-wrapper {
          padding:0;
        }
        .profile-card-root[data-focus-mode="true"] .card-image-stage {
          max-width:100% !important;
          background:transparent;
        }

        /* NEW: Make the card image flush & fully visible in focus mode */
        .profile-card-root[data-focus-mode="true"] .card-stage-wrapper,
        .profile-card-root[data-focus-mode="true"] .card-stage {
          display:flex;
        }
        .profile-card-root[data-focus-mode="true"] .card-stage {
          align-items:center;
          justify-content:center;
        }
        .profile-card-root[data-focus-mode="true"] .card-image-stage {
          aspect-ratio:auto;
          height:100%;
          max-height:100%;
          width:auto;
          max-width:100%;
          display:flex;
          align-items:center;
          justify-content:center;
        }
        .profile-card-root[data-focus-mode="true"] .card-image-stage > button {
          position:static !important;
          inset:auto !important;
          width:auto;
          height:100%;
          display:flex;
          align-items:center;
          justify-content:center;
        }
        .profile-card-root[data-focus-mode="true"] .card-image-stage > button > img {
          position:static !important;
          width:auto !important;
          height:100% !important;
          max-height:100%;
          object-fit:contain !important;
        }
      `}</style>
    </article>
  );
}

/* (Optional) Rank badge helper */
function RankBadgeLite({ text }: { text?: string | null }) {
  if (!text) return null;
  return (
    <span className="ml-2 rounded-full border border-white/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/70">
      {text}
    </span>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wide text-white/50 mb-2">
      {title}
    </div>
  );
}

function ChipRow({
  values,
  tone = "slate",
  tight = false,
}: {
  values: string[];
  tone?:
    | "slate"
    | "purple"
    | "rose"
    | "indigo"
    | "cyan"
    | "teal"
    | "emerald"
    | "amber"
    | "sky";
  tight?: boolean;
}) {
  if (!values.length) return null;
  const toneMap: Record<string, string> = {
    slate: "bg-white/10 border-white/15 text-white/80",
    purple: "bg-purple-500/20 border-purple-400/40 text-purple-200",
    rose: "bg-rose-500/20 border-rose-400/40 text-rose-200",
    indigo: "bg-indigo-500/20 border-indigo-400/40 text-indigo-200",
    cyan: "bg-cyan-500/20 border-cyan-400/40 text-cyan-200",
    teal: "bg-teal-500/20 border-teal-400/40 text-teal-200",
    emerald: "bg-emerald-500/20 border-emerald-400/40 text-emerald-200",
    amber: "bg-amber-500/25 border-amber-400/50 text-amber-100",
    sky: "bg-sky-500/25 border-sky-400/50 text-sky-100",
  };
  const cls = toneMap[tone] || toneMap.slate;
  return (
    <div
      className={`flex flex-wrap gap-${tight ? "1" : "2"} ${
        tight ? "text-[10px]" : "text-[11px]"
      }`}
    >
      {values.map((v) => (
        <span
          key={v}
          className={`rounded-full px-2 py-0.5 border ${cls} ${
            !tight && "leading-tight"
          }`}
        >
          {v}
        </span>
      ))}
    </div>
  );
}

/* -------- Focused Profile Modal (portal shell) -------- */
function FocusedProfileModal(props: { children: React.ReactNode; onClose: () => void; ariaLabel: string }) {
  const { children, onClose, ariaLabel } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const f = ref.current.querySelectorAll<HTMLElement>(
          'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])'
        );
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center p-2 sm:p-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        className="relative w-[96vw] max-w-[1280px] h-[92vh] overflow-hidden rounded-2xl border border-white/15 bg-slate-950/85 backdrop-blur-md shadow-2xl focus:outline-none"
        tabIndex={-1}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-50 rounded-md bg-black/50 hover:bg-black/70 text-white px-3 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-400"
        >
          Close
        </button>
        <div className="w-full h-full overflow-auto custom-scrollbar pr-1">{children}</div>
      </div>
    </div>
  );
}

/* -------- Image Lightbox Modal -------- */
function ImageLightbox(props: {
  card: MiniCard;
  tab: string;
  onClose: () => void;
  onAddToWishlist: () => void;
  onAddToCollection: () => void;
}) {
  const { card, onClose, onAddToWishlist, onAddToCollection } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const focusables = ref.current.querySelectorAll<HTMLElement>(
          'a,button,[tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (document.activeElement === first && e.shiftKey) {
          e.preventDefault();
          last.focus();
        } else if (document.activeElement === last && !e.shiftKey) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

const img =
  scryImgFromId(card.scryfall_id, "normal") ||
  "/images/sg-card-back.png";

  return (
    <div
      className="fixed inset-0 z-[3500] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={card.name || "Card image"}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        className="relative flex flex-col items-center gap-4"
      >
        {/* Close button (floating) */}
        <button
          onClick={onClose}
          className="absolute -top-6 right-0 md:top-0 md:-right-10 rounded-md bg-black/60 hover:bg-black/80 text-white text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-sky-400"
        >
          ✕
        </button>

        {/* Card image only, scaled to 75% of viewport so it fits fully */}
        <img
          src={img}
          alt={card.name || "Card"}
          className="select-none pointer-events-none rounded-lg shadow-xl max-h-[75vh] max-w-[75vw] w-auto h-auto object-contain"
          draggable={false}
        />

        {/* Floating action buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-1">
          <a
            href={img}
            download={(card.name || "card").replace(/\s+/g, "_") + ".jpg"}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-white/15 hover:bg-white/25 text-white focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            Download
          </a>
          <button
            onClick={onAddToWishlist}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-amber-500/90 hover:bg-amber-400 text-black focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            Add to Wishlist
          </button>
          <button
            onClick={onAddToCollection}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-emerald-500/90 hover:bg-emerald-400 text-black focus:outline-none focus:ring-2 focus:ring-emerald-300"
          >
            Add to Collection
          </button>
        </div>
      </div>
    </div>
  );
}

